import { useMemo } from 'react'
import { DateTime } from 'luxon'
import { PatientHistoryType } from 'src/graphql.generated'
import { usePanelPatientUid } from '../dashboard/panelContext'
import { formatTime, humanizeRole, toIso } from '../format'
import { valueOf } from '../panels/patientHistory/EntryBody'
import { isNutritionIntakeNote } from '../panels/patientHistory/eventLogger/advancedNoteKinds'
import { humanizeKey } from '../panels/patientHistory/HistoryValueTable'
import { labelForHistoryType } from '../panels/patientHistory/typeLabels'
import { useCompletions, useReflections } from './selectors'
import { PatientHistoryEntry, useAdvancedPatientHistory } from './useAdvancedPatientHistory'
import { AdvancedContentCompletion, AdvancedReflection } from './types'
import { ActivityBlock } from '../activityBlock'

const SCORED_SURVEY_TYPES = new Set<PatientHistoryType>([
  PatientHistoryType.Phq9,
  PatientHistoryType.Gad7,
  PatientHistoryType.Who5,
  PatientHistoryType.Asrm,
  PatientHistoryType.Isi,
  PatientHistoryType.Ocir,
  PatientHistoryType.Pcl5,
])

const SESSION_TYPES: PatientHistoryType[] = [
  PatientHistoryType.VideoCall,
  PatientHistoryType.IntakeNote,
  PatientHistoryType.IntakeNoteCoaching,
  PatientHistoryType.IntakeNoteComprehensive,
  PatientHistoryType.IntakeNotePsychiatry,
]

const localDay = (iso: string): string => DateTime.fromISO(iso).toFormat('yyyy-MM-dd')

const fromCompletion = (completion: AdvancedContentCompletion): ActivityBlock => ({
  id: `lesson-${completion.completionId}`,
  type: 'lesson',
  title: completion.title,
  day: localDay(completion.completedAt),
  at: completion.completedAt,
  indicators: [{ label: 'Track', value: humanizeRole(completion.trackKey) }],
  description: `Completed at ${formatTime(completion.completedAt)}.`,
})

// PHI: `body` is the patient's own words — rendered on screen and nowhere else.
const fromReflection = (reflection: AdvancedReflection): ActivityBlock => ({
  id: `reflection-${reflection.reflectionId}`,
  type: 'reflection',
  title: reflection.title ? `Reflection: ${reflection.title}` : 'Reflection',
  day: localDay(reflection.createdAt),
  at: reflection.createdAt,
  indicators: [{ label: 'Track', value: humanizeRole(reflection.trackKey) }],
  description: reflection.prompt,
  details: [reflection.body],
})

const scoreOf = (value: unknown): string | null => {
  if (typeof value !== 'object' || value === null) {
    return null
  }
  const entry = Object.entries(value).find(([key]) => key.endsWith('_SCORE'))

  return entry && entry[1] !== null && entry[1] !== undefined ? String(entry[1]) : null
}

const fromHistory = (entry: PatientHistoryEntry): ActivityBlock | null => {
  const at = toIso(entry.date) ?? toIso(entry.createdAt)
  if (!at || !entry.type) {
    return null
  }
  const base = {
    id: `history-${entry.entryID}`,
    day: localDay(at),
    at,
    title: labelForHistoryType(entry.type, valueOf(entry)),
  }

  if (SCORED_SURVEY_TYPES.has(entry.type)) {
    const score = 'value' in entry ? scoreOf(entry.value) : null

    return {
      ...base,
      type: 'survey',
      indicators: score ? [{ label: 'Score', value: score }] : [],
      description: 'Symptom survey submitted.',
    }
  }

  if (SESSION_TYPES.includes(entry.type) || isNutritionIntakeNote(valueOf(entry))) {
    const durationMs = 'videoCall' in entry ? entry.videoCall?.activeDurationInMS : null

    return {
      ...base,
      type: 'session',
      indicators: durationMs ? [{ label: 'Duration', value: `${Math.round(durationMs / 60000)} min` }] : [],
      description:
        'videoCall' in entry && entry.videoCall?.reason
          ? humanizeKey(entry.videoCall.reason)
          : 'Session with the care team.',
    }
  }

  return null
}

// Advanced's completions and reflections plus `server`'s scored surveys and sessions, one list by time.
export const mergeActivity = (
  completions: AdvancedContentCompletion[],
  reflections: AdvancedReflection[],
  history: PatientHistoryEntry[]
): ActivityBlock[] =>
  [
    ...completions.map(fromCompletion),
    ...reflections.map(fromReflection),
    ...history.map(fromHistory).filter((block): block is ActivityBlock => block !== null),
  ].sort((a, b) => DateTime.fromISO(a.at).toMillis() - DateTime.fromISO(b.at).toMillis())

export type ActivityByDay = ReadonlyMap<string, ActivityBlock[]>

export const groupByDay = (blocks: ActivityBlock[]): ActivityByDay => {
  const byDay = new Map<string, ActivityBlock[]>()
  blocks.forEach((block) => byDay.set(block.day, [...(byDay.get(block.day) ?? []), block]))

  return byDay
}

export const usePatientActivity = (): ActivityByDay => {
  const uid = usePanelPatientUid()
  const completions = useCompletions()
  const reflections = useReflections()
  const history = useAdvancedPatientHistory(uid)

  return useMemo(
    () => groupByDay(mergeActivity(completions, reflections, history)),
    [completions, reflections, history]
  )
}
